import {
  redirect,
  Navigate,
  createRoute,
  createRouter,
  createRootRoute,
} from '@tanstack/react-router';
import App from '../App';
import NotFoundPage from '../pages/not-found';
import { AuthProvider, userCan } from '../lib/services/auth.service';
import LoginPage from '@/pages/login';
import ErrorPage from '@/pages/error';
import AdminPartnersPage from '@/pages/admin/partners';
import OrgsPage from '@/pages/orgs';
import PartnerPage from '@/pages/partner';
import PartnersUsersPage from '@/pages/partner-users';
import RootUsersPage from '@/pages/admin/root-users';
import LogoutPage from '@/pages/logout';

const rootRoute = createRootRoute({
  component: App,
});

/*
 * This check is technically a failsafe because of the authentication check that happens
 * in @/src/context/app/index.tsx. Read the comment in that file in the primary useEffect
 * hook for more details.
 */
const requireAuth = () => {
  if (!AuthProvider.user) {
    throw redirect({
      to: '/login',
    });
  }
};

const requireManageAdmins = () => {
  if (!userCan(AuthProvider.user, 'manage-admins')) {
    throw redirect({
      to: '/orgs',
    });
  }
};

const requireSuper = () => {
  if (!userCan(AuthProvider.user, 'super')) {
    throw redirect({
      to: '/orgs',
    });
  }
};

const routes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: () => <Navigate to="/orgs" />,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/orgs',
    component: OrgsPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/partner',
    component: PartnerPage,
    beforeLoad: requireAuth,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin-users',
    component: PartnersUsersPage,
    beforeLoad: requireManageAdmins,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/partners',
    component: AdminPartnersPage,
    beforeLoad: requireSuper,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/root-users',
    component: RootUsersPage,
    beforeLoad: requireSuper,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    component: LoginPage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/logout',
    component: LogoutPage,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/error',
    component: ErrorPage,
    beforeLoad: () => ({ layout: 'public' }),
    validateSearch: ({ code }: Record<string, unknown>): { code?: number } => {
      return {
        code: code ? Number(code) : undefined,
      };
    },
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '*',
    component: NotFoundPage,
  }),
];

const routeTree = rootRoute.addChildren(routes);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export default router;
